import React, { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Download, Search, RefreshCw } from "lucide-react";

/**
 * Admin · Communications · Client Leads
 * Spreadsheet-style view of every /get-help submission with a CSV export.
 */
const COLS = [
  { k: "created_at", label: "Date", fmt: (v) => (v ? new Date(v).toLocaleString() : "") },
  { k: "name", label: "Name" },
  { k: "email", label: "Email" },
  { k: "whatsapp", label: "WhatsApp" },
  { k: "preferred_contact", label: "Prefers" },
  { k: "domain", label: "Domain" },
  { k: "assistance", label: "Assistance" },
  { k: "level", label: "Level" },
  { k: "target_journal", label: "Target journal" },
  { k: "has_title", label: "Has title" },
  { k: "title", label: "Working title" },
  { k: "notes", label: "Notes" },
  { k: "status", label: "Status" },
];

export default function AdminLeadsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/leads/client-help");
      setRows(r.data || []);
    } catch { toast.error("Couldn't load leads"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    if (!ql) return rows;
    return rows.filter((r) =>
      COLS.some((c) => String(r[c.k] || "").toLowerCase().includes(ql))
    );
  }, [rows, q]);

  const downloadCsv = () => {
    const esc = (v) => {
      const s = String(v ?? "");
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const header = COLS.map((c) => c.label).join(",");
    const body = filtered.map((r) =>
      COLS.map((c) => esc(c.fmt ? c.fmt(r[c.k]) : r[c.k] ?? "")).join(",")
    ).join("\n");
    const blob = new Blob([header + "\n" + body], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `khojbyte-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div data-testid="admin-leads-page">
      <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">ADMIN · COMMUNICATIONS</div>
      <div className="flex items-end justify-between flex-wrap gap-3 mt-1">
        <h1 className="font-serif text-3xl lg:text-4xl text-kb-ink">
          Client <em className="italic text-kb-primary">leads</em>
          <span className="kb-mono text-xs text-kb-ink-muted ml-3">{filtered.length} / {rows.length}</span>
        </h1>
        <div className="flex items-center gap-2">
          <button onClick={load} className="kb-btn-outline text-xs inline-flex items-center gap-1" data-testid="admin-leads-refresh"><RefreshCw className="w-3.5 h-3.5" /> Refresh</button>
          <button onClick={downloadCsv} disabled={filtered.length === 0} className="kb-btn-primary text-xs inline-flex items-center gap-1" data-testid="admin-leads-download"><Download className="w-3.5 h-3.5" /> Download CSV</button>
        </div>
      </div>

      <div className="mt-4 relative max-w-sm">
        <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-kb-ink-muted" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search any column…"
          className="kb-input text-sm pl-8" data-testid="admin-leads-search" />
      </div>

      <div className="mt-6 kb-card overflow-hidden">
        {loading ? (
          <div className="p-6 kb-mono text-xs text-kb-ink-muted">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-6 kb-mono text-xs text-kb-ink-muted">No leads yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
                <tr className="text-left">
                  {COLS.map((c) => (
                    <th key={c.k} className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted px-3 py-2 whitespace-nowrap">{c.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, i) => (
                  <tr key={r.id || i} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`admin-leads-row-${r.id || i}`}>
                    {COLS.map((c) => {
                      const v = c.fmt ? c.fmt(r[c.k]) : r[c.k];
                      return (
                        <td key={c.k} className="px-3 py-2 align-top whitespace-nowrap">
                          <div className="max-w-[260px] truncate" title={String(v ?? "")}>{v || <span className="text-kb-ink-light">—</span>}</div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
